import axios, { type AxiosInstance } from 'axios';

/**
 * Header cart count for the multi-product cart (CPS-7694). Deliberately not
 * CartManagementProvider: that pipeline is checkout-grade (get-or-create,
 * currency sync, address/tax/proration) and must not run per page view.
 */

const CART_STORE = 'asset';

/** How long a count is served from cache before a mount or focus refetches it. */
export const CART_COUNT_STALE_TIME_MS = 60_000;

const BASE_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 60_000;
/** Retries after the first failure; the badge stays hidden once exhausted. */
export const MAX_CART_COUNT_RETRIES = 5;

export type CartCountIdentity = {
  userId: string;
  organizationId: string;
};

/** The subset of the bridge cart the badge reads. */
type BridgeCart = {
  lineItems?: unknown[];
};

/**
 * `ensureExists=false` is what keeps this a read: without it the bridge
 * creates an empty cart for the user, and every page view would mint one.
 */
export const buildCartCountPath = ({
  userId,
  organizationId,
}: CartCountIdentity): string =>
  `/commerce/bridge/v1/users/${encodeURIComponent(userId)}/organizations/${encodeURIComponent(organizationId)}/cart?store=${CART_STORE}&ensureExists=false`;

/**
 * Distinct products, like the ASv2 `ItemCount` the badge showed so far — not
 * `totalLineItemQuantity`, which counts seats.
 */
export const countCartItems = (cart: BridgeCart | null | undefined): number =>
  Array.isArray(cart?.lineItems) ? cart.lineItems.length : 0;

const statusOf = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

/** No active cart — never created, or ordered by a completed checkout. */
export const fetchCartCount = async (
  client: AxiosInstance,
  identity: CartCountIdentity
): Promise<number> => {
  try {
    const { data } = await client.get<BridgeCart>(buildCartCountPath(identity));
    const count = countCartItems(data);
    if (count === 0) {
      checkoutDeparted = false;
    }
    return count;
  } catch (error) {
    if (statusOf(error) === 404) {
      checkoutDeparted = false;
      return 0;
    }
    throw error;
  }
};

/**
 * `Retry-After` is either delay-seconds or an HTTP date (RFC 9110 §10.2.3);
 * a date is turned into the wait remaining from `now`.
 */
export const retryAfterMs = (error: unknown, now = Date.now()): number => {
  const header = axios.isAxiosError(error)
    ? error.response?.headers?.['retry-after']
    : undefined;
  if (typeof header !== 'string' || header.trim() === '') {
    return 0;
  }
  const seconds = Number(header);
  const ms = Number.isNaN(seconds) ? Date.parse(header) - now : seconds * 1000;
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
};

/**
 * Exponential backoff for the 1-based retry SWR is about to make (1s, 2s, 4s,
 * … capped at 60s), stretched to the gateway's `Retry-After` when it asks for
 * longer.
 */
export const cartCountRetryDelay = (
  retryCount: number,
  error?: unknown
): number => {
  const retryAfter = retryAfterMs(error);
  // Retry-After may be hidden by CORS. Keep the five fallback retries spread
  // over at least a minute while preserving exponential backoff.
  const baseDelay =
    statusOf(error) === 429 && !retryAfter
      ? BASE_RETRY_DELAY_MS * 2
      : BASE_RETRY_DELAY_MS;
  return Math.max(
    Math.min(baseDelay * 2 ** (retryCount - 1), MAX_RETRY_DELAY_MS),
    retryAfter
  );
};

let readsHeldUntil = 0;

/**
 * Holds every cart-count read until `until` (epoch ms) — focus, cart-changed,
 * checkout return and remounts, not just SWR's scheduled retry — so none of
 * them spends another request against the rate limit before Retry-After or
 * the backoff has run out. `0` releases the hold.
 */
export const holdCartCountReads = (until: number): void => {
  readsHeldUntil = until;
};

/** SWR `isPaused` for the cart count read. */
export const areCartCountReadsHeld = (now = Date.now()): boolean =>
  now < readsHeldUntil;

/**
 * Rate limits, server errors and dropped connections can clear up on their
 * own; any other 4xx will not, so retrying it only burns rate limit.
 */
export const shouldRetryCartCount = (
  error: unknown,
  retryCount: number
): boolean => {
  if (retryCount > MAX_CART_COUNT_RETRIES) {
    return false;
  }
  const status = statusOf(error);
  return status === undefined || status === 429 || status >= 500;
};

/**
 * A 429 is expected and handled (badge hidden, backoff); auth errors are left
 * out just as the global SWRProvider leaves them out. Everything else goes to
 * Sentry.
 */
export const shouldReportCartCountError = (error: unknown): boolean => {
  const status = statusOf(error);
  return status !== 429 && status !== 401 && status !== 403;
};

/**
 * Returning from checkout with Back restores this page from bfcache with the
 * pre-checkout count in memory; an ordered cart reads as 404, so revalidating
 * here is what drops the badge to 0. A regular navigation back loads a fresh
 * document, which fetches on mount anyway.
 */
export const subscribeToPageRestore = (listener: () => void) => {
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      listener();
    }
  };
  window.addEventListener('pageshow', onPageShow);
  return () => window.removeEventListener('pageshow', onPageShow);
};

let checkoutDeparted = false;

/** The cart link can open checkout in another tab, leaving this page mounted. */
export const markCheckoutDeparture = () => {
  checkoutDeparted = true;
};

/** Refresh each return until a read observes an empty cart, even inside stale time. */
export const subscribeToCheckoutReturn = (listener: () => void) => {
  const onFocus = () => {
    if (checkoutDeparted) {
      listener();
    }
  };
  window.addEventListener('focus', onFocus);
  return () => window.removeEventListener('focus', onFocus);
};
